import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Collapse, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

interface MobileMenuActionDockProps {
  drawerOpen: boolean;
  onCloseDrawer: () => void;
  onRequest: () => void;
}

const PHONE_OPTIONS = [
  {
    label: 'Шанхай',
    display: '+86 155-021-063-10',
    href: 'tel:+8615502106310',
  },
  { label: 'Россия', display: '+7 910 935-11-52', href: 'tel:+79109351152' },
];

const REQUEST_DELAY_MS = 225;

const MobileMenuActionDock: React.FC<MobileMenuActionDockProps> = ({
  drawerOpen,
  onCloseDrawer,
  onRequest,
}) => {
  const theme = useTheme();
  const [phoneMenuOpen, setPhoneMenuOpen] = useState(false);
  const requestTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!drawerOpen) {
      setPhoneMenuOpen(false);
    }
  }, [drawerOpen]);

  useEffect(
    () => () => {
      if (requestTimerRef.current !== null) {
        window.clearTimeout(requestTimerRef.current);
      }
    },
    []
  );

  const closeForSystemAction = () => {
    setPhoneMenuOpen(false);
    onCloseDrawer();
  };

  const handleRequest = () => {
    setPhoneMenuOpen(false);
    onCloseDrawer();
    if (requestTimerRef.current !== null) {
      window.clearTimeout(requestTimerRef.current);
    }
    requestTimerRef.current = window.setTimeout(onRequest, REQUEST_DELAY_MS);
  };

  const actionButtonSx = {
    minHeight: 52,
    minWidth: 0,
    px: 0.5,
    borderRadius: '14px',
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    backgroundColor: alpha(theme.palette.background.paper, 0.72),
    textTransform: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
    fontSize: '0.68rem',
    fontWeight: 700,
    '& .MuiSvgIcon-root': { fontSize: 20 },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      borderColor: alpha(theme.palette.primary.main, 0.45),
    },
  } as const;

  return (
    <Box
      data-testid="mobile-menu-action-dock"
      sx={{
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        px: 1.5,
        pt: 1,
        pb: 'max(12px, env(safe-area-inset-bottom))',
        backgroundColor: alpha(theme.palette.background.paper, 0.96),
        backdropFilter: 'blur(12px)',
      }}
    >
      <Collapse in={phoneMenuOpen} unmountOnExit>
        <Box
          id="mobile-menu-phone-options"
          role="group"
          aria-label="Выбор телефона"
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            mb: 1,
            p: 1,
            borderRadius: '14px',
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          }}
        >
          {PHONE_OPTIONS.map((phone) => (
            <Button
              key={phone.href}
              component="a"
              href={phone.href}
              onClick={closeForSystemAction}
              aria-label={`Позвонить: ${phone.label}`}
              sx={{
                minHeight: 44,
                borderRadius: '12px',
                color: theme.palette.text.primary,
                textTransform: 'none',
                flexDirection: 'column',
                lineHeight: 1.2,
              }}
            >
              <Typography
                component="span"
                sx={{ fontSize: '0.72rem', fontWeight: 800 }}
              >
                {phone.label}
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: '0.66rem', color: 'text.secondary' }}
              >
                {phone.display}
              </Typography>
            </Button>
          ))}
        </Box>
      </Collapse>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 0.75,
        }}
      >
        <Button
          onClick={() => setPhoneMenuOpen((open) => !open)}
          aria-expanded={phoneMenuOpen}
          aria-controls="mobile-menu-phone-options"
          sx={actionButtonSx}
        >
          <CallOutlinedIcon />
          Позвонить
        </Button>

        <Button
          component="a"
          href="mailto:2608197979@mail.ru"
          onClick={closeForSystemAction}
          sx={actionButtonSx}
        >
          <EmailOutlinedIcon />
          Email
        </Button>
        <Button onClick={handleRequest} sx={actionButtonSx}>
          <ChatBubbleOutlineIcon />
          Заявка
        </Button>
      </Box>
    </Box>
  );
};

export default MobileMenuActionDock;
